import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import StoryCard from "@/components/feature/StoryCard";
import { stories, storyCategories } from "@/mocks/stories";

export default function Stories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("category") || "全部";

  const filtered = useMemo(() => {
    if (active === "全部") return stories;
    return stories.filter((story) => story.category === active);
  }, [active]);

  const selectCategory = (category: string) => {
    if (category === "全部") {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "亲述档案｜回声墙 网暴黑料亲历者自述合集",
    description:
      "收录网暴黑料亲历者的完整自述，涵盖言语围攻、人肉搜索、恶意剪辑、造谣诽谤、举报封禁与舆论株连六类真实处境。",
    inLanguage: "zh-CN",
    hasPart: stories.map((story) => ({
      "@type": "Article",
      headline: story.title,
      author: { "@type": "Person", name: story.author },
      datePublished: story.date,
      articleSection: story.category,
    })),
  };

  return (
    <>
      <title>亲述档案｜网暴黑料亲历者自述合集 - 回声墙</title>
      <meta
        name="description"
        content="分为言语围攻、人肉搜索、恶意剪辑、造谣诽谤、举报封禁与舆论株连六类的网暴黑料亲历者自述合集，完整记录当事人的心理变化与伤害后果。"
      />
      <meta name="keywords" content="网暴黑料受害者,网暴黑料当事人,亲述,网暴黑料伤害,网暴黑料影响" />
      <link rel="canonical" href="/stories" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="亲述档案｜网暴黑料亲历者自述合集 - 回声墙" />
      <meta
        property="og:description"
        content="六类真实处境，完整记录网暴黑料当事人的心理变化与伤害后果。"
      />
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>

      <section className="bg-secondary-950 pt-28 md:pt-36 pb-14 md:pb-16">
        <div className="max-w-editorial mx-auto px-4 md:px-8">
          <div className="[&_a]:text-background-200/70 [&_span]:text-background-100/80">
            <Breadcrumb items={[{ label: "亲述档案" }]} />
          </div>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight">
            亲述档案
          </h1>
          <p className="mt-5 max-w-3xl text-sm md:text-base leading-relaxed text-background-200/85">
            这里的每一篇文字，都来自经历过网暴黑料的当事人。他们中有教师、学生、店主、母亲与普通上班族。
            我们把口述内容整理成可读的长文，希望读者看到的不是猎奇，而是一条伤害发生的完整链条。
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-2.5">
            {storyCategories.map((category) => {
              const isActive = category === active;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => selectCategory(category)}
                  aria-pressed={isActive}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    isActive
                      ? "bg-accent-500 text-secondary-950"
                      : "border border-background-100/25 text-background-200 hover:border-accent-400 hover:text-accent-400"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-center justify-between gap-4 mb-8">
            <p className="text-sm text-foreground-600">
              当前分类：<span className="font-medium text-foreground-900">{active}</span>，共{" "}
              <span className="font-medium text-foreground-900">{filtered.length}</span> 篇亲述
            </p>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((story) => (
                <StoryCard key={story.id} story={story} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center border border-dashed border-background-300 rounded-lg">
              <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              <p className="mt-3 text-sm text-foreground-600">该分类下暂无档案，试试其他分类。</p>
            </div>
          )}

          <div className="mt-14 bg-background-100 border border-background-200 rounded-lg p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                你也有想被记录下来的经历吗
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600 max-w-2xl">
                在充分了解你的意愿之前，我们不会公开任何信息。你可以先匿名来信，讲述你愿意讲述的部分。
              </p>
            </div>
            <Link
              to="/about#help"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-mail-send-line"></i>
              联系编辑部
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}